import React, { ReactNode } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid2,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tile from 'components/Tile/Tile';
import PersonalLinkAndQRCode from 'components/PersonalLinkAndQRCode';
import { REACT_APP_OCA_BASE_URL, S3_BUCKET_NAME } from 'config';
import { Account, LenderProfile, UserProfile } from 'types';
import { useRelatedOcaLinksDataFetch } from './hooks/useRelatedOcaLinksDataFetch';
import { ModalsContainer } from 'components/shared/Modals';
import { ModalsKeys } from 'global';
import { mapFinanceProgramEntityToFormValues, PreviewFinanceProgramModal } from 'modules/financeProgram';
import { useModal } from 'hooks/useModal';
import { FinanceProgram } from '@trnsact/trnsact-shared-types';
import { QuoteProgramDialog } from 'modules/financeProgram/ui/QuoteProgramDialog';
import { keyBy } from 'utils/arrayUtils';

interface Props {
  userDetails: UserProfile;
}

export default function DealerRelatedLdcOcaLinks({ userDetails }: Props) {
  const classes = useStyles();
  const history = useHistory();

  const account = useSelector((state: any) => state.account) as Account;
  const lenderProfiles = useSelector((state: any) => state.lp) as LenderProfile[];

  const { handleOpen: handleOpenQuoteProgramDialog } = useModal(ModalsKeys.QuoteProgramDialog);
  const { handleOpen: handleOpenFinanceProgramPreviewModal } = useModal(ModalsKeys.PreviewFinanceForm);

  const { lenderProfilesWithOcaAccess, financeProgramsByLenderProfileId, isLoading } = useRelatedOcaLinksDataFetch(
    account.id
  );

  const handleFinanceProgramClick = (financeProgram: FinanceProgram) => {
    const program = mapFinanceProgramEntityToFormValues(financeProgram, lenderProfiles as any, []);
    handleOpenFinanceProgramPreviewModal({
      program,
      lpsWithOcaAccessByLenderProfileId: keyBy(lenderProfilesWithOcaAccess, 'lenderProfileId'),
    });
  };

  const onQuoteProgramConfirm = (lenderProfileId: string, financeProgramId: string) => {
    history.push({
      pathname: '/startDeal',
      search: `?lenderProfileId=${lenderProfileId}&financeProgramId=${financeProgramId}&vendorContactId=${userDetails.dynamicsContactId}`,
    });
  };

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.QuoteProgramDialog]: <QuoteProgramDialog />,
    [ModalsKeys.PreviewFinanceForm]: <PreviewFinanceProgramModal />,
  };

  return (
    <>
      <Tile title="Lender Dealer Personal Credit Application Links">
        {lenderProfilesWithOcaAccess?.length ? (
          <Grid2 container>
            {lenderProfilesWithOcaAccess.map(lenderProfile => {
              const financePrograms = financeProgramsByLenderProfileId?.[lenderProfile.lenderProfileId] ?? [];
              return (
                <Accordion key={lenderProfile.lenderProfileId}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${lenderProfile.lenderProfileId}-content`}
                    id={`${lenderProfile.lenderProfileId}-header`}
                  >
                    <Typography variant="h5">{lenderProfile.name}</Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid2 container spacing={2}>
                      <Grid2 size={2}>
                        {lenderProfile.logo && (
                          <img
                            alt="Lender Logo"
                            style={{
                              maxHeight: '250px',
                              maxWidth: '100%',
                            }}
                            src={`https://${S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${lenderProfile.logo}`}
                          />
                        )}
                      </Grid2>
                      <Grid2 size={4}>
                        {financePrograms.length && (
                          <Box display="flex" flexDirection="column" alignItems="flex-start">
                            <Button
                              onClick={() =>
                                handleOpenQuoteProgramDialog({
                                  financePrograms,
                                  lenderProfileId: lenderProfile.lenderProfileId,
                                  onConfirm: onQuoteProgramConfirm,
                                })
                              }
                            >
                              Quote Program
                            </Button>
                            <Box mt={1}>
                              <Typography fontWeight="bold">Finance Programs:</Typography>
                              <List dense>
                                {financePrograms.map(fp => (
                                  <ListItem disablePadding>
                                    <ListItemText
                                      primary={
                                        <Link
                                          className={classes.finProgramLink}
                                          onClick={() => handleFinanceProgramClick(fp)}
                                        >
                                          {fp.nameInternal}
                                        </Link>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          </Box>
                        )}
                      </Grid2>
                      <Grid2 size={6} className={classes.QRCode}>
                        <PersonalLinkAndQRCode
                          personalLink={`${REACT_APP_OCA_BASE_URL}/credit-app?lenderProfileId=${lenderProfile.lenderProfileId}&vendorcontactId=${userDetails.dynamicsContactId}`}
                          linkHeader="Personal Lender Dealer Credit Application Link"
                        />
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Grid2>
        ) : isLoading ? (
          <LinearProgress style={{ width: '100%' }} />
        ) : (
          <h3>No Related Lenders with Lender Dealer Oca Access</h3>
        )}
      </Tile>
      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
}

const useStyles = makeStyles(theme => ({
  QRCode: {
    '& .MuiGrid-grid-sm-6.personalLinkAndQRCodeRoot': {
      maxWidth: '100%',
    },
  },
  finProgramLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
}));
